import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { AlertTriangle, CheckCircle2, Clock, FileText, Loader2, Mail, RefreshCw, ShoppingCart } from "lucide-react";

import { api, purchaseBundle } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

const STORAGE_KEY = "ce_dashboard_email";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("nl-BE", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso?.slice(0, 10) || "—";
  }
}

function StatusBadge({ status }) {
  const map = {
    completed: { label: "Klaar", cls: "bg-emerald-100 text-emerald-700" },
    processing: { label: "Bezig", cls: "bg-amber-100 text-amber-700" },
    queued: { label: "In wachtrij", cls: "bg-slate-100 text-slate-700" },
    failed: { label: "Mislukt", cls: "bg-red-100 text-red-700" },
  };
  const s = map[status] || { label: status || "—", cls: "bg-slate-100 text-slate-600" };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${s.cls}`}>{s.label}</span>;
}

export default function MijnChecks() {
  const { toast } = useToast();
  const [email, setEmail] = useState(localStorage.getItem(STORAGE_KEY) || "");
  const [input, setInput] = useState(localStorage.getItem(STORAGE_KEY) || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [renewing, setRenewing] = useState(false);


  useEffect(() => {
    if (!email) return;
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      try {
        const { data: d } = await api.get(`/credits/dashboard/${encodeURIComponent(email)}`);
        if (!cancelled) setData(d);
      } catch (err) {
        if (!cancelled) toast({ title: "Kon dashboard niet laden", description: err?.message || "Probeer opnieuw", variant: "destructive" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [email, toast]);

  async function reload() {
    if (!email) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(`/credits/dashboard/${encodeURIComponent(email)}`);
      setData(d);
    } catch (err) {
      toast({ title: "Kon dashboard niet laden", description: err?.message || "Probeer opnieuw", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }

  function submit(e) {
    e?.preventDefault();
    const clean = input.trim().toLowerCase();
    if (!clean) return;
    localStorage.setItem(STORAGE_KEY, clean);
    setEmail(clean);
  }

  function signOut() {
    localStorage.removeItem(STORAGE_KEY);
    setEmail("");
    setInput("");
    setData(null);
  }

  async function renewOneClick() {
    if (!email) return;
    setRenewing(true);
    try {
      const res = await purchaseBundle({ email, pack_id: "pack-1" });
      if (res?.payment_url) {
        window.location.href = res.payment_url;
      } else {
        toast({ title: "Kon betaling niet starten", variant: "destructive" });
      }
    } catch (err) {
      toast({ title: "Vernieuwen mislukt", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    } finally {
      setRenewing(false);
    }
  }

  const expiringSoon = useMemo(
    () => (data?.bundles || []).filter((b) => b.expiring_soon && b.credits_remaining > 0),
    [data],
  );

  return (
    <main className="max-w-5xl mx-auto px-6 py-16" data-testid="mijn-checks-page">
      <Helmet>
        <title>Mijn checks — CheckExpert</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <header className="mb-10">
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-ink tracking-tight">Mijn checks</h1>
        <p className="text-ink-soft mt-2 text-base sm:text-lg">Overzicht van je credits, verloopdata en alle uitgevoerde controles.</p>
      </header>

      {!email ? (
        <form onSubmit={submit} className="max-w-md rounded-2xl bg-white border border-border p-6 space-y-4" data-testid="email-form">
          <Label htmlFor="dash-email">Vul je e-mailadres in</Label>
          <Input id="dash-email" type="email" value={input} onChange={(e) => setInput(e.target.value)} placeholder="jij@voorbeeld.be" required data-testid="email-input" />
          <button type="submit" className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white font-semibold" data-testid="email-submit">
            <Mail className="h-4 w-4" /> Toon mijn checks
          </button>
          <p className="text-xs text-ink-soft">We tonen alle checks en credits gekoppeld aan dit adres.</p>
        </form>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <p className="text-sm text-ink-soft">
              Ingelogd als <span className="font-semibold text-ink" data-testid="current-email">{email}</span>
            </p>
            <div className="flex gap-2">
              <button onClick={reload} disabled={loading} className="text-xs text-ink-soft hover:text-ink inline-flex items-center gap-1" data-testid="refresh-btn">
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Ververs
              </button>
              <button onClick={signOut} className="text-xs text-ink-soft hover:text-ink" data-testid="signout-btn">Ander e-mailadres</button>
            </div>
          </div>

          {/* Expiring credits warning */}
          {expiringSoon.length > 0 && (
            <section className="mb-8 rounded-2xl bg-amber-50 border border-amber-200 p-6" data-testid="expiry-warning">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h2 className="font-display text-xl font-semibold text-ink">Credits verlopen binnenkort</h2>
                  <ul className="mt-2 space-y-1 text-sm text-ink-soft">
                    {expiringSoon.map((b) => (
                      <li key={b.bundle_id}>
                        <strong className="text-ink">{b.credits_remaining}</strong> credit{b.credits_remaining > 1 ? "s" : ""} uit &ldquo;{b.pack_label}&rdquo; — nog {b.days_until_expiry} dagen geldig (tot {fmtDate(b.expires_at)})
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={renewOneClick}
                    disabled={renewing}
                    className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#002FA7] hover:bg-[#0033B5] text-white text-sm font-semibold disabled:opacity-60"
                    data-testid="renew-btn"
                  >
                    {renewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    Vernieuw nu — €4,99
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* Credit overview */}
          <section className="grid sm:grid-cols-2 gap-4 mb-10">
            <div className="rounded-2xl bg-white border border-border p-6" data-testid="credits-summary">
              <p className="text-xs uppercase tracking-wider font-semibold text-ink-soft">Beschikbare credits</p>
              <p className="font-display text-5xl font-bold text-ink mt-2" data-testid="credits-count">{data?.credits_available ?? "—"}</p>
              <p className="text-sm text-ink-soft mt-1">Goed voor evenveel checks.</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link to="/credits" className="inline-flex items-center gap-2 text-sm text-[#002FA7] font-semibold" data-testid="buy-more">
                  <ShoppingCart className="h-4 w-4" /> Koop meer credits
                </Link>
              </div>
            </div>

            <div className="rounded-2xl bg-white border border-border p-6">
              <p className="text-xs uppercase tracking-wider font-semibold text-ink-soft">Uitgevoerde checks</p>
              <p className="font-display text-5xl font-bold text-ink mt-2" data-testid="checks-count">{data?.recent_checks?.length ?? 0}</p>
              <p className="text-sm text-ink-soft mt-1">Totaal aantal analyses uitgevoerd.</p>
            </div>
          </section>

          {/* Bundles list */}
          {(data?.bundles || []).length > 0 && (
            <section className="mb-10" data-testid="bundles-section">
              <h2 className="font-display text-2xl font-semibold text-ink mb-4">Mijn bundels</h2>
              <div className="rounded-2xl bg-white border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-secondary text-ink-soft text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-4 py-3 text-left">Pakket</th>
                      <th className="px-4 py-3 text-left">Credits over</th>
                      <th className="px-4 py-3 text-left">Geldig tot</th>
                      <th className="px-4 py-3 text-left">Resterend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.bundles.map((b) => (
                      <tr key={b.bundle_id} className="border-t border-border" data-testid={`bundle-row-${b.bundle_id}`}>
                        <td className="px-4 py-3 font-semibold text-ink">{b.pack_label}</td>
                        <td className="px-4 py-3 text-ink-soft">{b.credits_remaining}</td>
                        <td className="px-4 py-3 text-ink-soft">{fmtDate(b.expires_at)}</td>
                        <td className="px-4 py-3">
                          {b.expiring_soon ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                              <Clock className="h-3 w-3" /> Nog {b.days_until_expiry} dagen
                            </span>
                          ) : (
                            <span className="text-ink-soft text-xs">Nog {b.days_until_expiry ?? "—"} dagen</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Recent checks */}
          <section data-testid="recent-checks-section">
            <h2 className="font-display text-2xl font-semibold text-ink mb-4">Recente checks</h2>
            {loading ? (
              <p className="text-ink-soft text-sm">Laden…</p>
            ) : (data?.recent_checks || []).length === 0 ? (
              <p className="text-ink-soft text-sm">Geen checks gevonden voor dit e-mailadres.</p>
            ) : (
              <div className="rounded-2xl bg-white border border-border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-secondary text-ink-soft text-xs uppercase tracking-wider">
                    <tr>
                      <th className="px-4 py-3 text-left">Checker</th>
                      <th className="px-4 py-3 text-left">Bestand</th>
                      <th className="px-4 py-3 text-left">Datum</th>
                      <th className="px-4 py-3 text-left">Score</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_checks.map((c) => (
                      <tr key={c.id} className="border-t border-border" data-testid={`check-row-${c.id}`}>
                        <td className="px-4 py-3 font-semibold text-ink">{c.checker_name || c.checker_slug}</td>
                        <td className="px-4 py-3 text-ink-soft truncate max-w-xs">
                          <span className="inline-flex items-center gap-1.5">
                            <FileText className="h-3.5 w-3.5" /> {c.source_filename || "Eigen tekst"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-ink-soft">{fmtDate(c.created_at)}</td>
                        <td className="px-4 py-3 font-bold text-ink">{c.analysis_status === "completed" ? `${c.score}/100` : "—"}</td>
                        <td className="px-4 py-3"><StatusBadge status={c.analysis_status} /></td>
                        <td className="px-4 py-3 text-right">
                          {c.analysis_status === "completed" ? (
                            <Link to={`/result/${c.id}`} className="text-[#002FA7] text-xs font-semibold inline-flex items-center gap-1" data-testid={`view-${c.id}`}>
                              <CheckCircle2 className="h-3.5 w-3.5" /> Bekijk
                            </Link>
                          ) : (
                            <span className="text-ink-soft text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </main>
  );
}
